export interface JobIntelUserProfile {
  id?: string;
  user_id: string;
  profile_complete: boolean;
  profile_completion_percentage: number;
  last_updated_at?: string;
  created_at?: string;
  setup_method?: string;

  // Part 1: Professional Background
  current_job_title?: string | null;
  current_industry?: string | null;
  years_of_experience?: number | null;
  experience_level?: string | null;
  education_level?: string | null;
  education_field?: string | null;
  certifications?: string[] | null;
  core_skills?: string[] | null;
  technical_skills?: string[] | null;
  leadership_skills?: string[] | null;
  industries_worked_in?: string[] | null;
  notable_employers?: string[] | null;
  career_summary?: string | null;
  resume_text?: string | null;
  linkedin_url?: string | null;

  // Part 2: Preferences and Targets
  target_job_titles?: string[] | null;
  target_job_title_variations?: string[] | null;
  target_seniority_level?: string | null;
  target_industries?: string[] | null;
  excluded_industries?: string[] | null;
  employment_type?: string[] | null;
  open_to_relocation?: boolean | null;
  relocation_cities?: string[] | null;
  current_city?: string | null;
  current_state?: string | null;
  current_zip?: string | null;
  max_commute_miles?: number | null;
  max_commute_minutes?: number | null;
  remote_preference?: string | null;
  hybrid_days_acceptable?: number | null;
  willing_to_travel?: boolean | null;
  max_travel_percentage?: number | null;
  salary_minimum_cents?: number | null;
  salary_target_cents?: number | null;
  open_to_variable_comp?: boolean | null;
  variable_comp_minimum_base_cents?: number | null;
  interested_in_equity?: boolean | null;
  currently_employed?: boolean | null;
  availability?: string | null;
  preferred_company_sizes?: string[] | null;
  preferred_company_structure?: string[] | null;
  target_companies?: string[] | null;
  excluded_companies?: string[] | null;
  required_keywords?: string[] | null;
  excluded_keywords?: string[] | null;
  required_benefits?: string[] | null;
  requires_visa_sponsorship?: boolean | null;
  work_schedule_requirements?: string | null;
  deal_breakers?: string | null;

  // Career Clarity
  career_clarity_report_id?: string | null;
  career_clarity_top_roles?: any | null;
  career_clarity_top_companies?: any | null;
  career_clarity_salary_data?: any | null;
  career_clarity_gap_analysis?: any | null;
  career_clarity_run_at?: string | null;
}

export interface JobIntelProfileDocument {
  id?: string;
  user_id: string;
  document_type: string;
  original_filename: string;
  file_size_bytes?: number | null;
  parse_status: string;
  parse_confidence_score?: number | null;
  fields_populated?: string[] | null;
  fields_inferred?: string[] | null;
  uploaded_at?: string;
  parsed_at?: string | null;
}

export type SetupMethod = 'documents_uploaded' | 'career_clarity_report' | 'manual' | 'tailorwiz_profile';

export type ExperienceLevel = 'entry' | 'mid' | 'senior' | 'director' | 'vp' | 'c_suite' | 'executive' | 'board';

export type EducationLevel = 'high_school' | 'some_college' | 'associates' | 'bachelors' | 'masters' | 'mba' | 'doctorate' | 'professional_license' | 'other';

export type RemotePreference = 'remote_only' | 'hybrid_preferred' | 'open_to_in_office' | 'no_preference';

export type Availability = 'immediately' | 'two_weeks' | 'thirty_days' | 'sixty_days' | 'ninety_days' | 'flexible';

export type CompanySize = 'startup' | 'small' | 'mid_size' | 'large' | 'enterprise';

export type CompanyStructure = 'public' | 'private' | 'nonprofit' | 'government';

export type EmploymentType = 'full_time' | 'part_time' | 'contract' | 'freelance' | 'interim' | 'fractional';

export type SeniorityLevel = 'individual_contributor' | 'manager' | 'director' | 'vp' | 'c_suite' | 'executive' | 'board';

export const EXPERIENCE_LEVEL_LABELS: Record<ExperienceLevel, string> = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior Level',
  director: 'Director',
  vp: 'Vice President',
  c_suite: 'C-Suite',
  executive: 'Executive',
  board: 'Board Level',
};

export const EDUCATION_LEVEL_LABELS: Record<EducationLevel, string> = {
  high_school: 'High School',
  some_college: 'Some College',
  associates: "Associate's Degree",
  bachelors: "Bachelor's Degree",
  masters: "Master's Degree",
  mba: 'MBA',
  doctorate: 'Doctorate',
  professional_license: 'Professional License',
  other: 'Other',
};

export const REMOTE_PREFERENCE_LABELS: Record<RemotePreference, string> = {
  remote_only: 'Remote Only',
  hybrid_preferred: 'Hybrid Preferred',
  open_to_in_office: 'Open to In-Office',
  no_preference: 'No Preference',
};

export const AVAILABILITY_LABELS: Record<Availability, string> = {
  immediately: 'Immediately',
  two_weeks: '2 Weeks',
  thirty_days: '30 Days',
  sixty_days: '60 Days',
  ninety_days: '90 Days',
  flexible: 'Flexible',
};

export const COMPANY_SIZE_LABELS: Record<CompanySize, string> = {
  startup: 'Startup (1-50)',
  small: 'Small (51-200)',
  mid_size: 'Mid-Size (201-1000)',
  large: 'Large (1001-5000)',
  enterprise: 'Enterprise (5000+)',
};

export const COMPANY_STRUCTURE_LABELS: Record<CompanyStructure, string> = {
  public: 'Public',
  private: 'Private',
  nonprofit: 'Non-Profit',
  government: 'Government',
};

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  full_time: 'Full-Time',
  part_time: 'Part-Time',
  contract: 'Contract',
  freelance: 'Freelance',
  interim: 'Interim',
  fractional: 'Fractional',
};

export const SENIORITY_LEVEL_LABELS: Record<SeniorityLevel, string> = {
  individual_contributor: 'Individual Contributor',
  manager: 'Manager',
  director: 'Director',
  vp: 'Vice President',
  c_suite: 'C-Suite',
  executive: 'Executive',
  board: 'Board Level',
};
